import { faTemperatureThreeQuarters } from "@fortawesome/pro-solid-svg-icons";
import {
  faChevronDown,
  faQuestion,
  faArrowsLeftRight,
  faCircleXmark
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import Freshness from "./Freshness";
import {
  getArea,
  convertTo12Hr,
  getAmPm,
  getDay,
  getTimeStamp,
  isInTargetWindow,
  isBeforeTargetWindow,
  isPastTargetWindow,
  isFresh,
  getOffset,
  checkFilter,
  isCurrentHour,
  getPLUPlan,
  getSentInHour
} from "./functions";

import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { joyrideStyles, joyrideStepsWaves } from "./helpers-joyride";
import { BakeryItem, BakeryTableProps } from "./../types";
import { useSnackbar } from "notistack";
import { fetchProductPlanner } from "../backendServices";
import { availRed, availAmber, wasteRed, wasteAmber } from "./variable-vault";
import TargetTime from "./TargetTime";
import Waves from "./Waves";
import TempCheck from "./TempCheck";
import { filter } from "lodash";

const BakeryTable: React.FC<BakeryTableProps> = ({
  bakeTableState,
  setBakeTableState,
  searchQueryState,
  isFOH,
  storeName,
  storeTrial,
  tempCheckTrial,
  handleShowFeedback,
  clearTempChecks
}) => {
  const saveData = () => {
    sessionStorage.setItem("bakeryItems", JSON.stringify(bakeTableState));
  };

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const adjustNewBakeryItems = (
    newBakeryItems: BakeryItem[],
    index: number,
    newAmount: number
  ) => {
    newBakeryItems[index].product_units = newAmount;
    return newBakeryItems;
  };

  const handleClear = (index: number) => {
    changeUnits(index, 0);
    window.dataLayer.push({
      event: "Clear units field",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      value: "(empty)",
      product: bakeTableState[index].product_name,
      time: getTimeStamp()
    });
  };

  const increment = (index: number, amount = 1) => {
    const newBakeryItems = [...bakeTableState];
    const newAmount =
      Math.floor((newBakeryItems[index].product_units + amount) / amount) *
      amount;
    setBakeTableState(adjustNewBakeryItems(newBakeryItems, index, newAmount));
    saveData();
    window.dataLayer.push({
      event: "Increment",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      product: newBakeryItems[index].product_name,
      value: amount.toString().padStart(3, "0"),
      time: getTimeStamp()
    });
    checkTooMuch(newAmount, newBakeryItems[index].product_name);
  };

  const decrement = (index: number, amount = 1) => {
    const newBakeryItems = [...bakeTableState];
    if (newBakeryItems[index].product_units > 0) {
      const newAmount =
        Math.ceil((newBakeryItems[index].product_units - amount) / amount) *
        amount;
      setBakeTableState(adjustNewBakeryItems(newBakeryItems, index, newAmount));
      saveData();
    }
    window.dataLayer.push({
      event: "Decrement",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      product: newBakeryItems[index].product_name,
      value: amount.toString().padStart(3, "0"),
      time: getTimeStamp()
    });
  };

  const handleUnitsChange = (
    index: number,
    event: { target: { value: string } }
  ) => {
    let newAmount = parseInt(event.target.value, 10) || 0;
    newAmount = newAmount < 0 ? newAmount * -1 : newAmount;
    changeUnits(index, newAmount);
  };

  const changeUnits = (index: number, newAmount: number) => {
    const newBakeryItems = [...bakeTableState];
    setBakeTableState(adjustNewBakeryItems(newBakeryItems, index, newAmount));
    saveData();
    window.dataLayer.push({
      event: "Type in units",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      product: newBakeryItems[index].product_name,
      value: newAmount.toString().padStart(3, "0"),
      time: getTimeStamp()
    });
    checkTooMuch(newAmount, newBakeryItems[index].product_name);
  };

  const checkTooMuch = (newAmount: number, product: string) => {
    if (newAmount > 70) {
      enqueueSnackbar(
        `Hold up! You are about to send ${product} x ${newAmount}. If this is not correct amend before sending to the till.
Please take to the bakers table to ensure great availability and freshness`,
        {
          variant: "error",
          transitionDuration: { enter: 0, exit: 0 },
          action: (key) => (
            <button onClick={() => closeSnackbar(key)}>Dismiss</button>
          ),
          style: {
            backgroundColor: "#CF2030",
            top: "50vh",
            transform: "translateY(-50%)"
          },
          persist: true,
          anchorOrigin: { horizontal: "center", vertical: "top" }
        }
      );
      window.dataLayer.push({
        event: "QTY warning popup activated",
        bakery: storeName,
        bakery_area: getArea(isFOH),
        value: newAmount.toString().padStart(3, "0"),
        time: getTimeStamp()
      });
    }
  };

  const [showTemp, setShowTemp] = useState(false);

  const [orderingBy, setOrderingBy] = useState<keyof BakeryItem>(
    storeTrial ? "waves_order" : "start_hour"
  );

  const [productPlanner, setProductPlanner] = useState<any>([]);

  const [currentHour, setCurrentHour] = useState(() => {
    const now = new Date();
    return now.getHours();
  });
  const [wideView, setWideView] = useState(false);
  const [joyrideRun, setJoyrideRun] = useState(false);

  const handleOrderBy = (type: keyof BakeryItem) => {
    orderBy(type);
    window.dataLayer.push({
      event: "Reorder by",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      value: type,
      time: getTimeStamp()
    });
  };

  const orderBy = (type: keyof BakeryItem) => {
    let bakeTableStateReOrdered;

    if (type == "already_made") {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) => {
        const aValue = a[type];
        const bValue = b[type];
        return bValue - aValue;
      });
    } else if (type == "start_hour") {
      const now = new Date();
      const currentHour = now.getHours();
      const end = "end_hour";
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) =>
        ("" + a["temp"]).localeCompare("" + b["temp"])
      );
      bakeTableStateReOrdered = [...bakeTableStateReOrdered].sort((a, b) => {
        if (a[end] <= currentHour && b[end] > currentHour) {
          return 1;
        } else if (a[end] > currentHour && b[end] <= currentHour) {
          return -1;
        } else {
          if (a[type] === b[type]) {
            return a[end] - b[end];
          }
          return a[type] - b[type];
        }
      });
    } else if (type == "waves_order") {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) => {
        const aValue = a[type] ?? 9999;
        const bValue = b[type] ?? 9999;
        return aValue - bValue;
      });
    } else if (type == "product_name" || type == "product_category") {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) =>
        ("" + a[type]).localeCompare("" + b[type])
      );
    } else {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) => {
        const aValue = a[type] ?? 0;
        const bValue = b[type] ?? 0;
        return bValue - aValue;
      });
    }

    setBakeTableState(bakeTableStateReOrdered);
    setOrderingBy(type);
  };

  const tempCheckHere = () => {
    return bakeTableState.some((item) => {
      const isCategoryMatch =
        (isFOH && item.product_category.includes("-FOH")) ||
        (!isFOH && !item.product_category.includes("-FOH"));

      return isCategoryMatch && item.temp_check;
    });
  };

  useEffect(() => {
    const checkHour = () => {
      const now = new Date();
      if (now.getHours() != currentHour) {
        setCurrentHour(now.getHours());
      }
      setTimeout(checkHour, 60000);
    };

    checkHour();

    orderBy(orderingBy);
  }, bakeTableState);

  const showTarget = () => {
    return bakeTableState.some((item) => {
      const isCategoryMatch =
        (isFOH && item.product_category.includes("-FOH")) ||
        (!isFOH && !item.product_category.includes("-FOH"));
      return isCategoryMatch && item.start_hour && item.end_hour;
    });
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    window.dataLayer.push({
      event: "Joyride Planner Support",
      value: status,
      step: data.index.toString(),
      type: type,
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });

    if (finishedStatuses.includes(status)) {
      setJoyrideRun(false);
    }
  };

  const [savedTempChecks, setSavedTempChecks] = useState<number[]>([]);

  const updateSavedTempChecks = (PLU: number) => {
    setSavedTempChecks((prevChecks) => [...prevChecks, PLU]);
  };

  const removeSavedTempCheck = (PLU: number) => {
    setSavedTempChecks((prevChecks) =>
      prevChecks.filter((check) => check !== PLU)
    );
    const newBakeryItems = [...bakeTableState];
    const index = newBakeryItems.findIndex((item) => item.PLU === PLU);
    if (index !== -1) {
      setBakeTableState(adjustNewBakeryItems(newBakeryItems, index, 0));
    }
  };

  const [onShiftInitials, setOnShiftInitials] = useState(
    localStorage.getItem("onSessionInitals") ?? ""
  );

  const updateOnShiftInitials = (initials: string) => {
    localStorage.setItem("onSessionInitals", initials);
    setOnShiftInitials(initials);
  };

  const tempCheckItem = (item: BakeryItem) => {
    return (
      item.temp_check === "Always" ||
      (item.temp_check === "Monday" &&
        getDay() === "Monday" &&
        !item.already_made) ||
      (item.temp_check === "Tuesday" &&
        getDay() === "Tuesday" &&
        !item.already_made) ||
      (item.temp_check === "Wednesday" &&
        getDay() === "Wednesday" &&
        !item.already_made) ||
      (item.temp_check === "Thursday" &&
        getDay() === "Thursday" &&
        !item.already_made) ||
      (item.temp_check === "Friday" &&
        getDay() === "Friday" &&
        !item.already_made) ||
      (item.temp_check === "Saturday" &&
        getDay() === "Saturday" &&
        !item.already_made) ||
      (item.temp_check === "Sunday" &&
        getDay() === "Sunday" &&
        !item.already_made)
    );
  };

  return (
    <>
      <Joyride
        steps={joyrideStepsWaves}
        styles={joyrideStyles}
        run={joyrideRun}
        showProgress={false}
        continuous
        callback={handleJoyrideCallback}
        debug={false}
        disableScrolling
        showSkipButton
        hideCloseButton
        locale={{ skip: "Quit", last: "Finish" }}
      />
      <table
        className={`bakery-table ${
          wideView ? "is-wide-view" : "is-narrow-view"
        }`}
        cellSpacing="0"
      >
        <thead>
          <tr className="bakery-table__header">
            <th
              className="bakery-table__name-column"
              onClick={() => handleOrderBy("product_name")}
            >
              <div className="h4">
                Name
                <FontAwesomeIcon
                  className={`jr-re-order ${
                    orderingBy == "product_name" ? "active" : ""
                  }`}
                  icon={faChevronDown}
                />
              </div>
            </th>
            <th
              className="bakery-table__category-column hlpr-hide-tablet-narrow"
              onClick={() => handleOrderBy("product_category")}
            >
              <div className="h4">
                Category
                <FontAwesomeIcon
                  className={orderingBy == "product_category" ? "active" : ""}
                  icon={faChevronDown}
                />
              </div>
            </th>
            {showTemp && !storeTrial ? (
              <th
                className="bakery-table__temp-column jr-temp"
                onClick={() => handleOrderBy("temp")}
              >
                <div>
                  <div className="h4">
                    <FontAwesomeIcon
                      className="temp-icon"
                      icon={faTemperatureThreeQuarters}
                    />
                    ºC
                  </div>
                  <FontAwesomeIcon
                    className={orderingBy == "temp" ? "active" : ""}
                    icon={faChevronDown}
                  />
                </div>
              </th>
            ) : null}
            {showTarget() && !storeTrial ? (
              <th
                className="bakery-table__target-column"
                onClick={() => handleOrderBy("start_hour")}
              >
                <div className="h4">
                  Target
                  <FontAwesomeIcon
                    className={
                      orderingBy == "start_hour"
                        ? "active jr-target"
                        : "jr-target"
                    }
                    icon={faChevronDown}
                  />
                </div>
              </th>
            ) : null}
            {!storeTrial ? (
              <th
                className="bakery-table__made-column"
                onClick={() => handleOrderBy("already_made")}
              >
                <div className="h4 jr-sent">
                  Sent
                  <FontAwesomeIcon
                    className={orderingBy == "already_made" ? "active" : ""}
                    icon={faChevronDown}
                  />
                </div>
              </th>
            ) : null}
            {storeTrial ? (
              <th className="h4 bakery-table__waves_column waves jr-waves">
                <div className="h4 waves__title">
                  Sent / forecast{" "}
                  <div className="waves-icons">
                    <FontAwesomeIcon
                      icon={faQuestion}
                      className="waves__help jr-waves-help"
                      onClick={() => {
                        setJoyrideRun(true);
                      }}
                    />
                    <FontAwesomeIcon
                      className={`waves__view jr-waves-view ${
                        wideView ? "hlpr-opacity-low" : ""
                      }`}
                      onClick={() => {
                        setWideView(!wideView);
                      }}
                      icon={faArrowsLeftRight}
                    />
                    <FontAwesomeIcon
                      className={orderingBy == "waves_order" ? "active" : ""}
                      icon={faChevronDown}
                      onClick={() => handleOrderBy("waves_order")}
                    />
                  </div>
                </div>
                <div className="hlpr-flexbox waves__header p centered jr-waves-head">
                  {Array.from({ length: 19 - 6 }, (_, i) => 6 + i).map((i) => (
                    <div
                      key={i}
                      onClick={() => {
                        const key = `P${i}` as keyof BakeryItem;
                        handleOrderBy(key);
                      }}
                      className={`wave-${isCurrentHour(
                        i,
                        currentHour
                      )} wave-d--${getOffset(currentHour, i)}${
                        orderingBy == `P${i}` ? " active" : ""
                      }`}
                    >
                      <div>
                        {convertTo12Hr(i)}
                        <small>{getAmPm(i)}</small>
                      </div>
                    </div>
                  ))}
                  <div className="waves__totals jr-waves-head-totals">
                    Totals
                  </div>
                </div>
              </th>
            ) : null}
            <th className="centered hlpr-fit-width">
              <div className="h4">Units</div>
            </th>
            {tempCheckTrial && tempCheckHere() && (
              <th className="centered hlpr-fit-width jr-temp-check-column">
                <div className="h4">Temp check</div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {bakeTableState.map((item, index) =>
            (isFOH && item.product_category.includes("-FOH")) ||
            (!isFOH && !item.product_category.includes("-FOH")) ? (
              <tr
                key={item.PLU}
                className={[
                  "bakery-table__row",
                  checkFilter(
                    item.product_name,
                    item.product_category,
                    searchQueryState
                  ),
                  item.product_units === 0 ? "" : "",
                  isPastTargetWindow(item.end_hour)
                    ? "bakery-table__row--past"
                    : "",
                  isBeforeTargetWindow(item.start_hour)
                    ? "bakery-table__row--before"
                    : "",
                  (isInTargetWindow(item.start_hour, item.end_hour) &&
                    item.already_made === 0) ||
                  (isInTargetWindow(item.start_hour, item.end_hour) &&
                    item.already_made > 0 &&
                    !isFresh(item.target_fresh, item.sent_date))
                    ? "bakery-table__row--missing"
                    : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <td className="jr-kpi-products">
                  <span className="h5 hlpr-cursor-pointer jr-kpi-products">
                    {item.product_name}
                  </span>
                </td>
                <td className="centered hlpr-hide-tablet-narrow">
                  {item.product_category.replace("-FOH", "")}
                </td>
                {showTemp && !storeTrial ? (
                  <td className="centered">{item.temp}</td>
                ) : null}
                {showTarget() && !storeTrial ? (
                  <TargetTime
                    startHour={item.start_hour}
                    endHour={item.end_hour}
                    sentDate={item.sent_date}
                    targetFresh={item.target_fresh}
                    alreadyMade={item.already_made}
                  />
                ) : null}
                {!storeTrial ? (
                  <td className="centered already-made jr-already-made">
                    <Waves
                      key={item.PLU}
                      startHour={item.start_hour}
                      endHour={item.end_hour}
                      sentDate={item.sent_date}
                      targetFresh={item.target_fresh}
                      alreadyMade={item.already_made}
                      alreadyMadeWaves={item.already_made_waves}
                      minWaves={item.min_waves ?? 0}
                      recWavesCount={0}
                    />
                    {item.sent_date && item.target_fresh ? (
                      <Freshness
                        targetFresh={item.target_fresh}
                        sentDate={item.sent_date}
                        endHour={item.end_hour}
                      />
                    ) : null}
                  </td>
                ) : null}
                {storeTrial ? (
                  <td className="centered waves">
                    <div className="hlpr-flexbox waves__body jr-waves-body">
                      {Array.from({ length: 19 - 6 }, (item, i) => 6 + i).map(
                        (i) => (
                          <div
                            key={i}
                            onClick={() => {
                              handleShowFeedback(
                                item.product_name,
                                item.PLU.toString(),
                                i.toString(),
                                getPLUPlan(item.PLU, productPlanner)
                                  ? getPLUPlan(item.PLU, productPlanner)[
                                      `H${i}`
                                    ]
                                  : 0,
                                getSentInHour(item, i) ?? 0
                              );
                            }}
                            className={`jr-slot wave-${isCurrentHour(
                              i,
                              currentHour
                            )} wave-d--${getOffset(currentHour, i)}`}
                          >
                            <div>
                              {getSentInHour(item, i) > 0
                                ? `${getSentInHour(item, i)} /`
                                : null}{" "}
                              {getPLUPlan(item.PLU, productPlanner)?.[
                                `H${i}`
                              ] != 0
                                ? getPLUPlan(item.PLU, productPlanner)?.[
                                    `H${i}`
                                  ]
                                : null}
                            </div>
                          </div>
                        )
                      )}
                      <div
                        className="waves__totals"
                        onClick={() => {
                          handleShowFeedback(
                            item.product_name,
                            item.PLU.toString(),
                            "Totals",
                            getPLUPlan(item.PLU, productPlanner)
                              ?.TotalPlanned ?? 0,
                            item.already_made
                          );
                        }}
                      >
                        {item.already_made > 0
                          ? `${item.already_made} /`
                          : null}{" "}
                        {getPLUPlan(item.PLU, productPlanner)?.TotalPlanned ??
                          null}
                      </div>
                    </div>
                  </td>
                ) : null}
                <td>
                  <div
                    className={`bakery-table__units jr-units${
                      tempCheckTrial &&
                      !savedTempChecks.includes(item.PLU) &&
                      tempCheckItem(item) &&
                      " bakery-table__units--temp-check-blocked"
                    }`}
                  >
                    {!storeTrial ? (
                      <button
                        className="red-button quantity-button"
                        onClick={() => decrement(index, 5)}
                      >
                        -5
                      </button>
                    ) : null}
                    <button
                      className="red-button quantity-button jr-units-steps"
                      onClick={() => decrement(index)}
                    >
                      -1
                    </button>
                    <div className="hlpr-relative">
                      <input
                        type="number"
                        min="0"
                        className="units units--input hlpr-input hlpr-input--with-clear jr-units-type"
                        onChange={(event) => handleUnitsChange(index, event)}
                        placeholder={item.product_units.toString()}
                        value={
                          item.product_units > 0
                            ? item.product_units.toString()
                            : ""
                        }
                      />
                      <FontAwesomeIcon
                        icon={faCircleXmark}
                        className="hlpr-clear-input clear-search jr-clear-search"
                        onClick={() => handleClear(index)}
                      />
                    </div>
                    <button
                      className="green-button quantity-button"
                      onClick={() => increment(index)}
                    >
                      +1
                    </button>
                    {!storeTrial ? (
                      <button
                        className="red-button quantity-button"
                        onClick={() => increment(index, 5)}
                      >
                        +5
                      </button>
                    ) : null}
                  </div>
                </td>
                {tempCheckTrial && tempCheckHere() && (
                  <td>
                    {tempCheckItem(item) && (
                      <TempCheck
                        key={onShiftInitials}
                        item={item}
                        isFOH={isFOH}
                        storeName={storeName}
                        updateSavedTempChecks={updateSavedTempChecks}
                        removeSavedTempCheck={removeSavedTempCheck}
                        onShiftInitials={onShiftInitials}
                        updateOnShiftInitials={updateOnShiftInitials}
                        bakeTableState={bakeTableState}
                        setBakeTableState={setBakeTableState}
                        clearTempChecks={clearTempChecks}
                      />
                    )}
                  </td>
                )}
              </tr>
            ) : null
          )}
        </tbody>
      </table>
    </>
  );
};

export default BakeryTable;
